import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import { getData } from '../utils/api';
import {
    toggleDataSetConfigSchedule,
    toggleDataSetConfigLiveMode,
    deleteDataSetConfig,
    buildProjectDataSourcesBaseUrl,
    updateDataSetConfigSchedule,
} from '../data-sources/api';
import { PROJECTS_API_BASE_ROUTE } from '../projects/api';
import { buildGetDataSourceStatusUrl } from '../operations/api';
import IngestionStatusChip from '../shared/components/IngestionStatusChip';
import { toRelativeTimeString } from '../utils/datetime-util';
import { defaultIngestionStatus } from '../shared/const';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    projectSelector: {
        marginBottom: theme.spacing(3),
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    bulkActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
    },
    bulkDivider: {
        width: 1,
        height: 28,
        backgroundColor: '#ccc',
        margin: theme.spacing(0, 0.5),
    },
    progressLabel: {
        color: theme.palette.text.secondary,
    },
    enabledChip: {
        backgroundColor: '#c8e6c9',
    },
    disabledChip: {
        backgroundColor: '#ffcdd2',
    },
    noData: {
        padding: theme.spacing(6),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    disableButton: {
        backgroundColor: '#d32f2f',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#b71c1c',
        },
    },
    tableWrapper: {
        border: '1px solid #e0e0e0',
        borderRadius: 4,
    },
    propChip: {
        margin: '1px 2px',
        fontSize: '0.65rem',
        height: 18,
        backgroundColor: '#e8eaf6',
        borderRadius: 3,
        maxWidth: '100%',
    },
    propCell: {
        maxWidth: 260,
        overflow: 'hidden',
    },
    selectedRow: {
        backgroundColor: '#e3f2fd !important',
    },
    checkCell: {
        width: 40,
        padding: '0 4px',
    },
}));

const toList = (data) => (Array.isArray(data) ? data : (data.items || []));

const parseProps = (jsonProperties) => {
    if (!jsonProperties) return {};
    try {
        return JSON.parse(jsonProperties);
    } catch (e) {
        return {};
    }
};

const rowKey = (row) => `${row.projectCode}__${row.dataSourceCode}__${row.id}`;

const mapDsToRows = (projectCode) => (ds) => (ds.dataSetConfigs || []).map((cfg) => {
    // eslint-disable-next-line no-console
    console.log('[BulkManager] raw cfg:', JSON.stringify(cfg));
    // Push datasets have no cron; pull datasets are cron-based.
    // pushMode may be set to PUSH_DISABLED on pull datasets too, so cron is
    // the authoritative discriminator.
    const hasCron = cfg.cron !== undefined && cfg.cron !== null && cfg.cron.trim() !== '';
    const hasPushMode = cfg.pushMode !== undefined
        && cfg.pushMode !== null
        && cfg.pushMode !== '';
    const isPush = !hasCron && hasPushMode;
    const isEnabled = isPush
        ? cfg.pushMode === 'PUSH_ENABLED'
        : Boolean(cfg.isCronEnabled);
    return {
        projectCode,
        dataSourceCode: ds.code,
        id: cfg.id,
        name: cfg.name || cfg.datasetName || '',
        label: cfg.label || '',
        cron: (cfg.cron || '').trim(),
        isCronEnabled: Boolean(cfg.isCronEnabled),
        isPush,
        isEnabled,
        rawPushMode: cfg.pushMode,
        properties: parseProps(cfg.jsonProperties),
    };
});

const applyToggle = (rows, projectCode, dataSourceCode, id, enabled) => rows.map((r) => {
    const match = r.projectCode === projectCode
        && r.dataSourceCode === dataSourceCode
        && r.id === id;
    if (!match) return r;
    return r.isPush
        ? { ...r, isEnabled: enabled }
        : { ...r, isCronEnabled: enabled, isEnabled: enabled };
});

const checkBoxBlankIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkBoxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

function LastRunCell({ statusMap, row }) {
    const runInfo = statusMap[
        `${row.projectCode}__${row.dataSourceCode}__${row.id}`
    ];
    const status = runInfo ? runInfo.status : defaultIngestionStatus;
    const ts = runInfo ? runInfo.timeStamp : '';
    return (
        <Tooltip
            title={ts ? toRelativeTimeString(ts) : 'Not available'}
            placement="top"
        >
            <span><IngestionStatusChip status={status} /></span>
        </Tooltip>
    );
}

function PropertiesCell({ properties, classes }) {
    const entries = Object.entries(properties);
    if (entries.length === 0) return <span>—</span>;
    return (
        <div className={classes.propCell}>
            {entries.map(([k, v]) => (
                <Tooltip key={k} title={`${k}: ${v}`}>
                    <Chip
                        label={`${k}: ${v}`}
                        size="small"
                        className={classes.propChip}
                    />
                </Tooltip>
            ))}
        </div>
    );
}

function DatasetBulkManager() {
    const classes = useStyles();

    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);

    const [allRows, setAllRows] = useState([]);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [loadingError, setLoadingError] = useState(null);

    const [nameFilter, setNameFilter] = useState('');
    const [modeFilter, setModeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dsFilter, setDsFilter] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

    const [selectedKeys, setSelectedKeys] = useState(new Set());

    const [statusMap, setStatusMap] = useState({});

    const [isBulkRunning, setIsBulkRunning] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
    const [bulkError, setBulkError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [cronTarget, setCronTarget] = useState(null);
    const [cronBulkRows, setCronBulkRows] = useState([]);
    const [cronBulkReadyRows, setCronBulkReadyRows] = useState([]);
    const [cronValue, setCronValue] = useState('');
    const [cronError, setCronError] = useState('');
    const [isSavingCron, setIsSavingCron] = useState(false);

    useEffect(() => {
        setLoadingProjects(true);
        getData(PROJECTS_API_BASE_ROUTE)
            .then((data) => setProjects(toList(data)))
            .catch(() => {})
            .finally(() => setLoadingProjects(false));
    }, []);

    const loadDatasets = useCallback((projectCodes) => {
        if (projectCodes.length === 0) {
            setAllRows([]);
            setSelectedKeys(new Set());
            return;
        }
        setLoadingDatasets(true);
        setLoadingError(null);
        setPage(0);
        setSelectedKeys(new Set());
        setStatusMap({});
        Promise.all(
            projectCodes.map(
                (code) => getData(buildProjectDataSourcesBaseUrl(code))
                    .then((data) => toList(data).flatMap(mapDsToRows(code)))
            )
        )
            .then((results) => {
                const rows = results.flat();
                setAllRows(rows);
                setDsFilter([]);

                // fetch monitoring status per unique datasource in the background
                const uniqueDs = [...new Map(
                    rows.map((r) => [
                        `${r.projectCode}__${r.dataSourceCode}`,
                        { projectCode: r.projectCode, dataSourceCode: r.dataSourceCode },
                    ])
                ).values()];

                Promise.all(
                    uniqueDs.map((ds) => getData(
                        buildGetDataSourceStatusUrl(ds.projectCode, ds.dataSourceCode)
                    )
                        .then((s) => ({ ds, data: s }))
                        .catch(() => ({ ds, data: null })))
                ).then((statuses) => {
                    const map = {};
                    statuses.forEach(({ ds, data }) => {
                        if (!data) return;
                        const configs = data.dataSetConfigs || [];
                        configs.forEach((cfg) => {
                            const k = `${ds.projectCode}__${ds.dataSourceCode}__${cfg.dataSetConfigId}`;
                            map[k] = cfg.lastRunInfo || { status: defaultIngestionStatus, timeStamp: '' };
                        });
                    });
                    setStatusMap(map);
                });
            })
            .catch(() => setLoadingError('Failed to load datasets. Please try again.'))
            .finally(() => setLoadingDatasets(false));
    }, []);

    useEffect(() => {
        loadDatasets(selectedProjects.map((p) => p.code));
    }, [selectedProjects, loadDatasets]);

    const filteredRows = allRows.filter((row) => {
        if (nameFilter.trim()) {
            const f = nameFilter.trim().toLowerCase();
            if (!row.name.toLowerCase().includes(f) && !row.label.toLowerCase().includes(f)) {
                return false;
            }
        }
        if (dsFilter.length > 0 && !dsFilter.includes(row.dataSourceCode)) return false;
        if (modeFilter === 'push' && !row.isPush) return false;
        if (modeFilter === 'pull' && row.isPush) return false;
        if (statusFilter === 'enabled' && !row.isEnabled) return false;
        if (statusFilter === 'disabled' && row.isEnabled) return false;
        return true;
    });

    const availableDs = [...new Set(allRows.map((r) => r.dataSourceCode))].sort();

    const pagedRows = filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const pagedKeys = pagedRows.map(rowKey);
    const selectedOnPage = pagedKeys.filter((k) => selectedKeys.has(k));
    const allPageSelected = pagedKeys.length > 0
        && selectedOnPage.length === pagedKeys.length;
    const somePageSelected = selectedOnPage.length > 0 && !allPageSelected;

    const handleFilterChange = (e) => {
        setNameFilter(e.target.value);
        setPage(0);
    };

    const handleModeFilterChange = (e, val) => {
        if (val !== null) { setModeFilter(val); setPage(0); }
    };

    const handleStatusFilterChange = (e, val) => {
        if (val !== null) { setStatusFilter(val); setPage(0); }
    };

    const handleClearFilters = () => {
        setNameFilter('');
        setModeFilter('all');
        setStatusFilter('all');
        setDsFilter([]);
        setPage(0);
    };

    const hasActiveFilters = nameFilter || modeFilter !== 'all' || statusFilter !== 'all'
        || dsFilter.length > 0;

    const handleChangePage = (e, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const toggleRowSelection = (key) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const togglePageSelection = () => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (allPageSelected) {
                pagedKeys.forEach((k) => next.delete(k));
            } else {
                pagedKeys.forEach((k) => next.add(k));
            }
            return next;
        });
    };

    const clearSelection = () => setSelectedKeys(new Set());

    const runToggle = (rows, enabled) => {
        // When enabling, intercept pull datasets that have no cron set
        if (enabled) {
            const needsCron = rows.filter((r) => !r.isPush && !r.cron);
            const ready = rows.filter((r) => r.isPush || r.cron);
            if (needsCron.length > 0) {
                setCronBulkRows(needsCron);
                setCronBulkReadyRows(ready);
                setCronValue('');
                setCronError('');
                return;
            }
        }
        setIsBulkRunning(true);
        setBulkProgress({ done: 0, total: rows.length });
        setBulkError(null);

        rows.reduce(
            (chain, row) => chain.then(
                () => {
                    const toggleFn = row.isPush
                        ? toggleDataSetConfigLiveMode
                        : toggleDataSetConfigSchedule;
                    return toggleFn({
                        project: row.projectCode,
                        dataSourceCode: row.dataSourceCode,
                        id: row.id,
                        enabled,
                    })
                        .then((response) => {
                            if (response.ok) {
                                setAllRows((prev) => applyToggle(
                                    prev,
                                    row.projectCode,
                                    row.dataSourceCode,
                                    row.id,
                                    enabled
                                ));
                            }
                        })
                        .catch(() => {})
                        .finally(() => {
                            setBulkProgress((p) => ({ ...p, done: p.done + 1 }));
                        });
                }
            ),
            Promise.resolve()
        )
            .catch(() => setBulkError('One or more toggles failed.'))
            .finally(() => setIsBulkRunning(false));
    };

    const handleBulkToggleAll = (enabled) => runToggle([...filteredRows], enabled);

    const handleBulkToggleSelected = (enabled) => {
        const rows = filteredRows.filter((r) => selectedKeys.has(rowKey(r)));
        runToggle(rows, enabled);
    };

    const handleToggle = (row, enabled) => {
        // Pull dataset with no cron being enabled — ask user to set a cron first
        if (!row.isPush && enabled && !row.cron) {
            setCronTarget(row);
            setCronValue('');
            setCronError('');
            return;
        }
        const toggleFn = row.isPush
            ? toggleDataSetConfigLiveMode
            : toggleDataSetConfigSchedule;
        toggleFn({
            project: row.projectCode,
            dataSourceCode: row.dataSourceCode,
            id: row.id,
            enabled,
        })
            .then((response) => {
                if (response.ok) {
                    setAllRows((prev) => applyToggle(
                        prev,
                        row.projectCode,
                        row.dataSourceCode,
                        row.id,
                        enabled
                    ));
                }
            })
            .catch(() => {});
    };

    const handleCronCancel = () => {
        setCronTarget(null);
        setCronBulkRows([]);
        setCronBulkReadyRows([]);
        setCronValue('');
        setCronError('');
    };
    const handleCronConfirm = () => {
        const trimmed = cronValue.trim();
        if (!trimmed) { setCronError('Please enter a cron expression.'); return; }

        // ── Single-row path ──────────────────────────────────────────────────
        if (cronTarget) {
            setIsSavingCron(true);
            setCronError('');
            updateDataSetConfigSchedule({
                project: cronTarget.projectCode,
                dataSourceCode: cronTarget.dataSourceCode,
                id: cronTarget.id,
                cron: trimmed,
                isCronEnabled: true,
            })
                .then((response) => {
                    if (response.ok) {
                        setAllRows((prev) => prev.map((r) => {
                            if (
                                r.projectCode === cronTarget.projectCode
                                && r.dataSourceCode === cronTarget.dataSourceCode
                                && r.id === cronTarget.id
                            ) {
                                return {
                                    ...r,
                                    cron: trimmed,
                                    isCronEnabled: true,
                                    isEnabled: true,
                                };
                            }
                            return r;
                        }));
                        setCronTarget(null);
                        setCronValue('');
                    } else {
                        setCronError('Failed to save cron. Please try again.');
                    }
                })
                .catch(() => setCronError('Failed to save cron. Please try again.'))
                .finally(() => setIsSavingCron(false));
            return;
        }

        // ── Bulk path ────────────────────────────────────────────────────────
        if (cronBulkRows.length > 0) {
            setIsSavingCron(true);
            setCronError('');
            const bulkRows = cronBulkRows;
            const readyRows = cronBulkReadyRows;
            // Save cron for every dataset that was missing one, sequentially
            bulkRows.reduce(
                (chain, row) => chain.then(() => updateDataSetConfigSchedule({
                    project: row.projectCode,
                    dataSourceCode: row.dataSourceCode,
                    id: row.id,
                    cron: trimmed,
                    isCronEnabled: true,
                }).then((response) => {
                    if (response.ok) {
                        setAllRows((prev) => prev.map((r) => {
                            if (
                                r.projectCode === row.projectCode
                                && r.dataSourceCode === row.dataSourceCode
                                && r.id === row.id
                            ) {
                                return {
                                    ...r,
                                    cron: trimmed,
                                    isCronEnabled: true,
                                    isEnabled: true,
                                };
                            }
                            return r;
                        }));
                    }
                }).catch(() => {})),
                Promise.resolve()
            ).then(() => {
                setCronBulkRows([]);
                setCronBulkReadyRows([]);
                setCronValue('');
                setCronTarget(null);
                // Now toggle the rows that already had a cron
                if (readyRows.length > 0) {
                    runToggle(readyRows, true);
                }
            })
                .catch(() => setCronError('One or more saves failed. Please try again.'))
                .finally(() => setIsSavingCron(false));
        }
    };

    const handleDeleteClick = (row) => setDeleteTarget(row);
    const handleDeleteCancel = () => setDeleteTarget(null);
    const handleDeleteConfirm = () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        deleteDataSetConfig({
            project: deleteTarget.projectCode,
            dataSourceCode: deleteTarget.dataSourceCode,
            id: deleteTarget.id,
        })
            .then((response) => {
                if (response.ok) {
                    setAllRows((prev) => prev.filter(
                        (r) => !(
                            r.projectCode === deleteTarget.projectCode
                            && r.dataSourceCode === deleteTarget.dataSourceCode
                            && r.id === deleteTarget.id
                        )
                    ));
                    setSelectedKeys((prev) => {
                        const next = new Set(prev);
                        next.delete(rowKey(deleteTarget));
                        return next;
                    });
                }
            })
            .catch(() => {})
            .finally(() => {
                setIsDeleting(false);
                setDeleteTarget(null);
            });
    };

    const enabledCount = filteredRows.filter((r) => r.isEnabled).length;
    const disabledCount = filteredRows.length - enabledCount;
    const selectionCount = selectedKeys.size;

    return (
        <div className={classes.root}>
            <Typography variant="h4" gutterBottom>
                BULK DATASET MANAGER
            </Typography>

            <div className={classes.projectSelector}>
                <Autocomplete
                    multiple
                    options={projects}
                    value={selectedProjects}
                    onChange={(e, value) => setSelectedProjects(value)}
                    disableCloseOnSelect
                    loading={loadingProjects}
                    getOptionLabel={(option) => option.name || option.code}
                    getOptionSelected={(option, value) => option.code === value.code}
                    renderOption={(option, { selected }) => (
                        <>
                            <Checkbox
                                icon={checkBoxBlankIcon}
                                checkedIcon={checkBoxCheckedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                color="primary"
                            />
                            {option.name || option.code}
                        </>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Select Projects"
                            placeholder={
                                selectedProjects.length === 0
                                    ? 'Search and select projects...'
                                    : ''
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingProjects
                                            ? <CircularProgress color="inherit" size={20} />
                                            : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderTags={(value, getTagProps) => value.map((option, index) => (
                        <Chip
                            label={option.name || option.code}
                            size="small"
                            color="primary"
                            {...getTagProps({ index })}
                        />
                    ))}
                />
                {selectedProjects.length > 0 && (
                    <Box mt={0.5}>
                        <Typography variant="caption" color="textSecondary">
                            {`${selectedProjects.length} project(s) selected — `}
                            <Button
                                size="small"
                                style={{ padding: 0, minWidth: 0 }}
                                onClick={() => setSelectedProjects(projects)}
                            >
                                Select all
                            </Button>
                            {' · '}
                            <Button
                                size="small"
                                style={{ padding: 0, minWidth: 0 }}
                                onClick={() => setSelectedProjects([])}
                            >
                                Clear
                            </Button>
                        </Typography>
                    </Box>
                )}
            </div>

            <Box className={classes.filterRow}>
                <TextField
                    label="Filter by dataset name"
                    variant="outlined"
                    size="small"
                    value={nameFilter}
                    onChange={handleFilterChange}
                    placeholder="e.g. matches, standings, seasons"
                    style={{ minWidth: 300 }}
                />
                <Autocomplete
                    multiple
                    options={availableDs}
                    value={dsFilter}
                    onChange={(e, val) => { setDsFilter(val); setPage(0); }}
                    disableCloseOnSelect
                    disabled={availableDs.length === 0}
                    renderOption={(option, { selected }) => (
                        <>
                            <Checkbox
                                icon={checkBoxBlankIcon}
                                checkedIcon={checkBoxCheckedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                            />
                            {option}
                        </>
                    )}
                    renderInput={(params) => (
                        <TextField
                            /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...params}
                            variant="outlined"
                            size="small"
                            label="Filter by data source"
                            placeholder={dsFilter.length === 0 ? 'All data sources' : ''}
                        />
                    )}
                    style={{ minWidth: 280 }}
                />
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="caption" color="textSecondary">
                        Mode:
                    </Typography>
                    <ToggleButtonGroup
                        value={modeFilter}
                        exclusive
                        onChange={handleModeFilterChange}
                        size="small"
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="push">Push</ToggleButton>
                        <ToggleButton value="pull">Pull</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="caption" color="textSecondary">
                        Status:
                    </Typography>
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={handleStatusFilterChange}
                        size="small"
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="enabled">Enabled</ToggleButton>
                        <ToggleButton value="disabled">Disabled</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                >
                    Clear Filters
                </Button>
                {allRows.length > 0 && (
                    <Typography variant="body2" color="textSecondary">
                        {`${filteredRows.length} total — ${enabledCount} enabled, ${disabledCount} disabled`}
                    </Typography>
                )}
            </Box>

            <Box className={classes.bulkActions}>
                <Typography variant="caption" style={{ fontWeight: 600, marginRight: 4 }}>
                    ALL FILTERED:
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={filteredRows.length === 0 || isBulkRunning}
                    onClick={() => handleBulkToggleAll(true)}
                >
                    {`Enable (${filteredRows.length})`}
                </Button>
                <Button
                    variant="contained"
                    className={classes.disableButton}
                    size="small"
                    disabled={filteredRows.length === 0 || isBulkRunning}
                    onClick={() => handleBulkToggleAll(false)}
                >
                    {`Disable (${filteredRows.length})`}
                </Button>

                <div className={classes.bulkDivider} />

                <Typography variant="caption" style={{ fontWeight: 600, marginRight: 4 }}>
                    SELECTED:
                </Typography>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={selectionCount === 0 || isBulkRunning}
                    onClick={() => handleBulkToggleSelected(true)}
                >
                    {`Enable (${selectionCount})`}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    style={selectionCount > 0
                        ? { color: '#d32f2f', borderColor: '#d32f2f' }
                        : {}}
                    disabled={selectionCount === 0 || isBulkRunning}
                    onClick={() => handleBulkToggleSelected(false)}
                >
                    {`Disable (${selectionCount})`}
                </Button>
                {selectionCount > 0 && (
                    <Button
                        size="small"
                        onClick={clearSelection}
                        disabled={isBulkRunning}
                    >
                        Clear selection
                    </Button>
                )}

                {isBulkRunning && (
                    <>
                        <CircularProgress size={18} />
                        <Typography variant="body2" className={classes.progressLabel}>
                            {`${bulkProgress.done} / ${bulkProgress.total}`}
                        </Typography>
                    </>
                )}
                {bulkError && (
                    <Typography variant="body2" color="error">
                        {bulkError}
                    </Typography>
                )}
                {loadingError && (
                    <Typography variant="body2" color="error">
                        {loadingError}
                    </Typography>
                )}
            </Box>

            {loadingDatasets && <LinearProgress />}

            {selectedProjects.length === 0 && !loadingDatasets && (
                <Typography className={classes.noData}>
                    Search and select one or more projects above to load their datasets.
                </Typography>
            )}

            {selectedProjects.length > 0 && !loadingDatasets && filteredRows.length === 0 && (
                <Typography className={classes.noData}>
                    No datasets found matching your filter.
                </Typography>
            )}

            {filteredRows.length > 0 && (
                <Paper className={classes.tableWrapper}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" className={classes.checkCell}>
                                        <Checkbox
                                            indeterminate={somePageSelected}
                                            checked={allPageSelected}
                                            onChange={togglePageSelection}
                                            size="small"
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell><strong>Project</strong></TableCell>
                                    <TableCell><strong>Data Source</strong></TableCell>
                                    <TableCell><strong>Dataset Name</strong></TableCell>
                                    <TableCell><strong>Label</strong></TableCell>
                                    <TableCell><strong>Cron</strong></TableCell>
                                    <TableCell><strong>Config Properties</strong></TableCell>
                                    <TableCell><strong>Last Run</strong></TableCell>
                                    <TableCell><strong>Scheduled</strong></TableCell>
                                    <TableCell><strong>Toggle</strong></TableCell>
                                    <TableCell><strong>Delete</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.map((row) => {
                                    const key = rowKey(row);
                                    const isSelected = selectedKeys.has(key);
                                    return (
                                        <TableRow
                                            key={key}
                                            hover
                                            selected={isSelected}
                                            className={isSelected ? classes.selectedRow : ''}
                                            onClick={() => toggleRowSelection(key)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell
                                                padding="checkbox"
                                                className={classes.checkCell}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleRowSelection(key)}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{row.projectCode}</TableCell>
                                            <TableCell>{row.dataSourceCode}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.label || '—'}</TableCell>
                                            <TableCell>
                                                {row.cron ? <code>{row.cron}</code> : '—'}
                                            </TableCell>
                                            <TableCell
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ maxWidth: 260, overflow: 'hidden' }}
                                            >
                                                <PropertiesCell
                                                    properties={row.properties}
                                                    classes={classes}
                                                />
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <LastRunCell statusMap={statusMap} row={row} />
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Chip
                                                    label={
                                                        row.isEnabled ? 'Enabled' : 'Disabled'
                                                    }
                                                    size="small"
                                                    className={
                                                        row.isEnabled
                                                            ? classes.enabledChip
                                                            : classes.disabledChip
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Switch
                                                    checked={row.isEnabled}
                                                    onChange={
                                                        (e) => handleToggle(row, e.target.checked)
                                                    }
                                                    color="primary"
                                                    size="small"
                                                    disabled={isBulkRunning}
                                                />
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Delete dataset config">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(row)}
                                                        disabled={isBulkRunning}
                                                        style={{ color: '#d32f2f' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredRows.length}
                        page={page}
                        onChangePage={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        labelDisplayedRows={
                            ({ from, to, count }) => `${from}–${to} of ${count}`
                        }
                    />
                </Paper>
            )}

            <Dialog open={Boolean(cronTarget) || cronBulkRows.length > 0} onClose={handleCronCancel} maxWidth="xs" fullWidth>
                <DialogTitle>Set Cron Expression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {cronBulkRows.length > 0 ? (
                            <>
                                <strong>{cronBulkRows.length}</strong>
                                {` dataset${cronBulkRows.length > 1 ? 's have' : ' has'} no cron expression set. Enter one expression to apply to all of them.`}
                                {cronBulkReadyRows.length > 0 && (
                                    <>
                                        {' The remaining '}
                                        <strong>{cronBulkReadyRows.length}</strong>
                                        {` dataset${cronBulkReadyRows.length > 1 ? 's' : ''} with an existing cron will be enabled as normal.`}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {'Dataset '}
                                <strong>{cronTarget ? (cronTarget.label || cronTarget.name) : ''}</strong>
                                {' has no cron expression set. Enter one to enable scheduling.'}
                            </>
                        )}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Cron expression"
                        placeholder="e.g. 0 * * * *"
                        fullWidth
                        variant="outlined"
                        value={cronValue}
                        onChange={(e) => { setCronValue(e.target.value); setCronError(''); }}
                        error={Boolean(cronError)}
                        helperText={cronError || 'Standard 5-field cron (min hour dom month dow)'}
                        disabled={isSavingCron}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCronCancel} disabled={isSavingCron}>Cancel</Button>
                    <Button
                        onClick={handleCronConfirm}
                        color="primary"
                        variant="contained"
                        disabled={isSavingCron}
                    >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isSavingCron ? 'Saving…' : cronBulkRows.length > 0 ? `Save & Enable (${cronBulkRows.length + cronBulkReadyRows.length})` : 'Save & Enable'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Dataset Config</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {'Delete '}
                        <strong>
                            {deleteTarget
                                ? (deleteTarget.label || deleteTarget.name)
                                : ''}
                        </strong>
                        {' from data source '}
                        <strong>{deleteTarget ? deleteTarget.dataSourceCode : ''}</strong>
                        ? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="secondary"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default DatasetBulkManager;
